import React from "react";
import { Link } from "react-router-dom";

import { Clock, Cog, Truck } from "../../../components/icons/svg/outline";
import {
  getProjectCustomerLabel,
  getProjectTitle,
} from "../../../utils/business";
import { getUserDisplayName } from "../../../utils/business/user";
import {
  emptyRfiCardSummary,
  emptyTmCardSummary,
} from "../shared/projectTicketSummaries";
import {
  ProjectRfiSummaryTile,
  ProjectTmSummaryTile,
} from "./CompactTicketSummary";
import {
  emptyProjectCardOpsSummary,
  formatProjectCardMetric,
  getProjectStatusConfig,
  PROJECT_HOME_CHIP_CLASS,
  PROJECT_HOME_MANAGER_CHIP_CLASS,
} from "./projectHomeHelpers";

/**
 * Compact concrete or labor metric box for the project card.
 *
 * @param {Object} props
 * @param {React.ComponentType<{className?: string}>} props.Icon - Leading icon.
 * @param {string} props.label - Box label (e.g. ``Concrete``).
 * @param {Array<{value: string, unit: string}>} props.metrics - Metric value/unit pairs.
 * @param {boolean} [props.isLoading=false] - Whether ops metrics are loading.
 * @returns {JSX.Element}
 */
function OpsMetricBox({ Icon, label, metrics, isLoading = false }) {
  return (
    <div className="min-w-0 rounded-lg bg-gray-100 px-2.5 py-2">
      <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {label}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs text-gray-500">
        {(metrics || []).map((metric) => (
          <span key={`${metric.unit}-${metric.value}`}>
            <span className="text-base font-bold tabular-nums text-gray-900">
              {isLoading ? "…" : metric.value}
            </span>{" "}
            {metric.unit}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Project summary card for the Projects landing page.
 *
 * Uses a real link so middle-click / ctrl-click open the project in a new tab.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.project - Project API row.
 * @param {(project: Object) => void} [props.onEdit] - Opens project edit modal.
 * @param {Object} [props.tmSummary] - Compact T&M status summary for this project.
 * @param {Object} [props.rfiSummary] - Compact RFI status summary for this project.
 * @param {boolean} [props.ticketsLoading=false] - Whether ticket lists are still loading.
 * @param {Object} [props.opsSummary] - Brief concrete + latest payroll-week labor totals.
 * @param {boolean} [props.opsLoading=false] - Whether concrete/labor metrics are loading.
 * @returns {JSX.Element}
 */
const ProjectCard = ({
  project,
  onEdit,
  tmSummary = emptyTmCardSummary(),
  rfiSummary = emptyRfiCardSummary(),
  ticketsLoading = false,
  opsSummary = emptyProjectCardOpsSummary(),
  opsLoading = false,
}) => {
  const status = getProjectStatusConfig(project, "header");
  const StatusIcon = status.Icon;
  const projectNumber =
    project.number || project.project_number || `Project ${project.id}`;
  const projectTitle = getProjectTitle(project);
  const managers = project.managers || [];
  const projectPath = `/projects/${project.id}`;
  const cardChipClassName = `${PROJECT_HOME_CHIP_CLASS} h-5 text-xs`;

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm shadow-gray-200 transition duration-200 hover:shadow-md hover:shadow-gray-300">
      {/* Real href so middle-click / open-in-new-tab work natively. */}
      <Link
        to={projectPath}
        className="absolute inset-0 z-0 rounded-xl"
        aria-label={`View ${projectTitle}`}
      />

      <div className="relative z-10 flex flex-1 flex-col pointer-events-none">
        <div className="flex items-start justify-between gap-2 px-3.5 pt-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium tabular-nums leading-none text-gray-500">
              {projectNumber}
            </div>
            <div className="mt-1 truncate text-base font-bold leading-snug tracking-tight text-gray-900">
              {projectTitle}
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-1.5 pt-0.5">
            <span
              className={`${cardChipClassName} whitespace-nowrap ${status.className}`}
            >
              <StatusIcon className="h-3 w-3" />
              {status.label}
            </span>
            {onEdit ? (
              <button
                type="button"
                className="pointer-events-auto inline-flex h-6 w-6 items-center justify-center rounded text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-secondary-100"
                aria-label={`Edit ${projectTitle}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onEdit(project);
                }}
              >
                <Cog className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2 px-3.5">
          <div className="min-w-0 flex-1 truncate text-xs font-normal text-gray-500">
            {getProjectCustomerLabel(project)}
          </div>
          <div className="flex h-5 flex-shrink-0 items-center justify-end gap-1">
            {managers.length > 0 ? (
              managers.map((manager) => {
                const name = getUserDisplayName(manager, "");
                if (!name) return null;
                return (
                  <span
                    key={manager.id || name}
                    title={name}
                    className={`${cardChipClassName} max-w-[7.5rem] truncate whitespace-nowrap ${PROJECT_HOME_MANAGER_CHIP_CLASS}`}
                  >
                    {name}
                  </span>
                );
              })
            ) : (
              <span
                className={`${cardChipClassName} whitespace-nowrap border-gray-200 bg-gray-50 text-gray-400`}
              >
                No manager
              </span>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 px-3.5">
          <OpsMetricBox
            Icon={Truck}
            label="Concrete"
            metrics={[
              {
                value: formatProjectCardMetric(opsSummary.concreteToday),
                unit: "CY today",
              },
              {
                value: formatProjectCardMetric(opsSummary.concrete7d),
                unit: "7d",
              },
            ]}
            isLoading={opsLoading}
          />
          <OpsMetricBox
            Icon={Clock}
            label="Labor"
            metrics={[
              {
                value: formatProjectCardMetric(
                  opsSummary.laborPayrollWeekHours
                ),
                unit: "Hrs (Latest Payroll Week)",
              },
            ]}
            isLoading={opsLoading}
          />
        </div>

        <div className="mt-3 flex flex-col gap-2 px-3.5 pb-3">
          <ProjectTmSummaryTile
            summary={tmSummary}
            isLoading={ticketsLoading}
          />
          <ProjectRfiSummaryTile
            summary={rfiSummary}
            isLoading={ticketsLoading}
          />
        </div>
      </div>
    </article>
  );
};

export default ProjectCard;
